/**
 * React hook for managing personas via the backend API.
 */

import { useCallback, useEffect } from 'react'
import { usePersonaStore } from '@/stores'
import type { Persona, AvatarConfig, WhatsAppPersonaConfig, CallingConfig, PaymentConfig } from '@/stores'
import { apiClient } from '@/services/apiClient'
import { voiceConfigFromApi, voiceConfigToApi } from '@/utils/voiceConfigMapper'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function transformAvatarConfig(raw: any): AvatarConfig | undefined {
  if (!raw) return undefined
  return {
    provider: raw.provider,
    replicaId: raw.replica_id,
    personaId: raw.persona_id,
    anamPersonaId: raw.anam_persona_id,
    anamAvatarId: raw.anam_avatar_id,
    anamVoiceId: raw.anam_voice_id,
    anamLlmId: raw.anam_llm_id,
    anamSystemPrompt: raw.anam_system_prompt,
    anamEnableRecording: raw.anam_enable_recording,
    anamEnableClientTools: raw.anam_enable_client_tools,
    anamLanguageCode: raw.anam_language_code,
    pipelineMode: raw.pipeline_mode,
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function serializeAvatarConfig(config: AvatarConfig): Record<string, any> {
  return {
    provider: config.provider,
    replica_id: config.replicaId,
    persona_id: config.personaId,
    anam_persona_id: config.anamPersonaId,
    anam_avatar_id: config.anamAvatarId,
    anam_voice_id: config.anamVoiceId,
    anam_llm_id: config.anamLlmId,
    anam_system_prompt: config.anamSystemPrompt,
    anam_enable_recording: config.anamEnableRecording,
    anam_enable_client_tools: config.anamEnableClientTools,
    anam_language_code: config.anamLanguageCode,
    pipeline_mode: config.pipelineMode,
  }
}

/** Transform snake_case calling_config from API → sparse camelCase
 * CallingConfig (SA-182: nulls/absent stay absent — densifying here is
 * what used to re-stamp sparse rows on every save). Retired keys
 * (enabled, max_call_duration_seconds, recording_consent_*, language)
 * are simply not mapped. */
function transformCallingConfig(raw: any): CallingConfig | undefined {
  if (!raw) return undefined
  const cfg: CallingConfig = {}
  if (raw.greeting_enabled != null) cfg.greetingEnabled = raw.greeting_enabled
  if (raw.greeting_message != null) cfg.greetingMessage = raw.greeting_message
  if (raw.goodbye_enabled != null) cfg.goodbyeEnabled = raw.goodbye_enabled
  if (raw.goodbye_message != null) cfg.goodbyeMessage = raw.goodbye_message
  if (raw.silence_prompt_enabled != null) cfg.silencePromptEnabled = raw.silence_prompt_enabled
  if (raw.silence_prompt_seconds != null) cfg.silencePromptSeconds = raw.silence_prompt_seconds
  if (raw.silence_prompt_message != null) cfg.silencePromptMessage = raw.silence_prompt_message
  if (raw.silence_hangup_seconds != null) cfg.silenceHangupSeconds = raw.silence_hangup_seconds
  if (raw.allow_barge_in != null) cfg.allowBargeIn = raw.allow_barge_in
  if (raw.stt_model != null) cfg.sttModel = raw.stt_model
  if (raw.tts_speed != null) cfg.ttsSpeed = raw.tts_speed
  return Object.keys(cfg).length ? cfg : undefined
}

/** Transform snake_case whatsapp_config from API → camelCase WhatsAppPersonaConfig */
export function transformWhatsAppConfig(raw: any): WhatsAppPersonaConfig | undefined {
  if (!raw) return undefined
  return {
    enabled: raw.enabled ?? false,
    greetingMessage: raw.greeting_message,
    autoReply: raw.auto_reply ?? true,
    voiceResponse: raw.voice_response ?? false,
    voiceMessageMode: raw.voice_message_mode ?? 'both',
    burstDebounceSeconds: raw.burst_debounce_seconds ?? undefined,
    maxHistoryMessages: raw.max_history_messages ?? 20,
    sessionTimeoutHours: raw.session_timeout_hours ?? 12,
    contextBridgingEnabled: raw.context_bridging_enabled ?? true,
    escalationConfig: raw.escalation_config ? {
      enabled: raw.escalation_config.enabled ?? false,
      triggerKeywords: raw.escalation_config.trigger_keywords,
    } : undefined,
    callingConfig: transformCallingConfig(raw.calling_config),
    timezone: raw.timezone ?? undefined,
  }
}

/** Transform camelCase CallingConfig → sparse snake_case for API
 * (SA-182: only explicitly set fields are sent; empty message strings
 * mean "back to default" and are dropped). */
function serializeCallingConfig(config: CallingConfig): Record<string, any> | null {
  const out: Record<string, any> = {}
  if (config.greetingEnabled !== undefined) out.greeting_enabled = config.greetingEnabled
  if (config.greetingMessage) out.greeting_message = config.greetingMessage
  if (config.goodbyeEnabled !== undefined) out.goodbye_enabled = config.goodbyeEnabled
  if (config.goodbyeMessage) out.goodbye_message = config.goodbyeMessage
  if (config.silencePromptEnabled !== undefined) out.silence_prompt_enabled = config.silencePromptEnabled
  if (config.silencePromptSeconds !== undefined) out.silence_prompt_seconds = config.silencePromptSeconds
  if (config.silencePromptMessage) out.silence_prompt_message = config.silencePromptMessage
  if (config.silenceHangupSeconds !== undefined) out.silence_hangup_seconds = config.silenceHangupSeconds
  if (config.allowBargeIn !== undefined) out.allow_barge_in = config.allowBargeIn
  if (config.sttModel) out.stt_model = config.sttModel
  if (config.ttsSpeed !== undefined) out.tts_speed = config.ttsSpeed
  return Object.keys(out).length ? out : null
}

/** Map the API `tags` value (array | null | absent) to the store shape. */
export function mapTagsFromApi(raw: unknown): string[] | undefined {
  return Array.isArray(raw) ? (raw as string[]) : undefined
}

/** Transform camelCase WhatsAppPersonaConfig → snake_case for API */
export function serializeWhatsAppConfig(config: WhatsAppPersonaConfig): Record<string, any> {
  return {
    enabled: config.enabled,
    greeting_message: config.greetingMessage || null,
    auto_reply: config.autoReply,
    voice_response: config.voiceResponse,
    voice_message_mode: config.voiceMessageMode ?? 'both',
    // Send null (omit) when undefined so the deployment default applies;
    // 0 is a real value (instant/disabled) and must round-trip.
    burst_debounce_seconds: config.burstDebounceSeconds ?? null,
    max_history_messages: config.maxHistoryMessages,
    session_timeout_hours: config.sessionTimeoutHours,
    context_bridging_enabled: config.contextBridgingEnabled,
    escalation_config: config.escalationConfig ? {
      enabled: config.escalationConfig.enabled,
      trigger_keywords: config.escalationConfig.triggerKeywords || [],
    } : null,
    calling_config: config.callingConfig ? serializeCallingConfig(config.callingConfig) : null,
    timezone: config.timezone ?? null,
  }
}


/** Transform snake_case payment_config from API → camelCase PaymentConfig */
export function transformPaymentConfig(raw: any): PaymentConfig | undefined {
  if (!raw) return undefined
  return {
    bankTransfer: raw.bank_transfer ? {
      enabled: raw.bank_transfer.enabled ?? false,
      beneficiaryName: raw.bank_transfer.beneficiary_name ?? undefined,
      bankName: raw.bank_transfer.bank_name ?? undefined,
      accountOrClabe: raw.bank_transfer.account_or_clabe ?? undefined,
      currency: raw.bank_transfer.currency ?? 'MXN',
      instructions: raw.bank_transfer.instructions ?? undefined,
      detailsImage: raw.bank_transfer.details_image ?? undefined,
      visionModel: raw.bank_transfer.vision_model ?? undefined,
      amountToleranceCents: raw.bank_transfer.amount_tolerance_cents ?? 0,
    } : undefined,
    card: raw.card ? { enabled: raw.card.enabled ?? false } : undefined,
  }
}

/** Transform camelCase PaymentConfig → snake_case for API */
export function serializePaymentConfig(config: PaymentConfig): Record<string, any> {
  return {
    bank_transfer: config.bankTransfer ? {
      enabled: config.bankTransfer.enabled,
      beneficiary_name: config.bankTransfer.beneficiaryName ?? null,
      bank_name: config.bankTransfer.bankName ?? null,
      account_or_clabe: config.bankTransfer.accountOrClabe ?? null,
      currency: config.bankTransfer.currency,
      instructions: config.bankTransfer.instructions ?? null,
      details_image: config.bankTransfer.detailsImage ?? null,
      vision_model: config.bankTransfer.visionModel ?? null,
      amount_tolerance_cents: config.bankTransfer.amountToleranceCents,
    } : null,
    card: config.card ? { enabled: config.card.enabled } : null,
  }
}

/**
 * An edit to a persona. A null WhatsApp or payment config turns that channel
 * off: `updatePersona` sends it as null, which clears the column, and the
 * store holds the cleared config as undefined (SA-1472).
 */
export type PersonaEdit = Omit<Partial<Persona>, 'whatsappConfig' | 'paymentConfig'> & {
  whatsappConfig?: WhatsAppPersonaConfig | null
  paymentConfig?: PaymentConfig | null
}

interface UsePersonasReturn {
  personas: Persona[]
  isLoading: boolean
  error: string | null
  fetchPersonas: (opts?: { includeArchived?: boolean }) => Promise<void>
  createPersona: (data: Omit<Persona, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Persona>
  updatePersona: (id: string, data: PersonaEdit) => Promise<Persona>
  /** Soft-delete by default. Pass `{ hard: true }` to permanently remove (admin only). */
  deletePersona: (id: string, opts?: { hard?: boolean }) => Promise<void>
  /** Restore a soft-deleted persona. */
  restorePersona: (id: string) => Promise<Persona>
  /** Duplicate a persona, returning the newly created copy. */
  duplicatePersona: (id: string) => Promise<Persona>
  /** Upload a profile picture for the persona; updates the store with the new avatarUrl. */
  uploadPersonaAvatar: (id: string, file: File) => Promise<Persona>
  /** Remove the persona's profile picture; clears avatarUrl in the store. */
  clearPersonaAvatar: (id: string) => Promise<void>
  /** Clear a persona's edit lock (owner or admin break-glass); updates the store. */
  unlockPersona: (id: string) => Promise<void>
}

export function usePersonas(): UsePersonasReturn {
  const {
    personas,
    isLoading,
    error,
    setPersonas,
    addPersona,
    updatePersona: updatePersonaInStore,
    deletePersona: deletePersonaFromStore,
    setLoading,
    setError,
  } = usePersonaStore()

  const fetchPersonas = useCallback(async (opts?: { includeArchived?: boolean }) => {
    setLoading(true)
    try {
      const url = opts?.includeArchived
        ? `/api/personas?include_archived=true`
        : `/api/personas`
      const response = await apiClient.fetch(url)
      if (!response.ok) {
        throw new Error(`Failed to fetch personas: ${response.statusText}`)
      }
      const data = await response.json()

      // Transform API response to match store format
      const transformed: Persona[] = data.map((p: any) => ({
        id: p.id,
        userId: p.user_id || undefined,
        name: p.name,
        description: p.description,
        avatarUrl: p.avatar_url,
        presentationMode: p.presentation_mode,
        voiceConfig: voiceConfigFromApi(p.voice_config),
        avatarConfig: transformAvatarConfig(p.avatar_config),
        ragConfig: {
          systemPrompt: p.rag_config.system_prompt,
          temperature: p.rag_config.temperature,
          maxTokens: p.rag_config.max_tokens,
          topK: p.rag_config.top_k,
          scoreThreshold: p.rag_config.score_threshold,
          serverUrl: p.rag_config.server_url,
          model: p.rag_config.model,
          apiKey: p.rag_config.api_key,
          graphId: p.rag_config?.graph_id,
        },
        currentBundleId: p.current_bundle_id ?? null,
        whatsappConfig: transformWhatsAppConfig(p.whatsapp_config),
        paymentConfig: transformPaymentConfig(p.payment_config),
        isShared: p.is_shared || false,
        hasShares: p.has_shares || false,
        isPublic: p.is_public || false,
        isLocked: p.is_locked || false,
        tags: mapTagsFromApi(p.tags),
        source: p.source || 'manual',
        externalId: p.external_id || undefined,
        externalMetadata: p.external_metadata || undefined,
        archivedAt: p.archived_at ? new Date(p.archived_at).getTime() : undefined,
        createdAt: new Date(p.created_at).getTime(),
        updatedAt: new Date(p.updated_at).getTime(),
      }))

      setPersonas(transformed)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch personas')
    }
  }, [setPersonas, setLoading, setError])

  const createPersona = useCallback(async (
    data: Omit<Persona, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<Persona> => {
    const body = {
      name: data.name,
      description: data.description,
      avatar_url: data.avatarUrl,
      presentation_mode: data.presentationMode,
      voice_config: voiceConfigToApi(data.voiceConfig),
      avatar_config: data.avatarConfig ? serializeAvatarConfig(data.avatarConfig) : null,
      rag_config: {
        system_prompt: data.ragConfig.systemPrompt,
        temperature: data.ragConfig.temperature,
        max_tokens: data.ragConfig.maxTokens,
        top_k: data.ragConfig.topK,
        score_threshold: data.ragConfig.scoreThreshold,
        server_url: data.ragConfig.serverUrl || null,
        model: data.ragConfig.model || null,
        api_key: data.ragConfig.apiKey || null,
        graph_id: data.ragConfig.graphId || null,
      },
      whatsapp_config: data.whatsappConfig ? serializeWhatsAppConfig(data.whatsappConfig) : null,
      payment_config: data.paymentConfig ? serializePaymentConfig(data.paymentConfig) : null,
      is_public: data.isPublic ?? false,
      tags: data.tags ?? null,
    }

    const response = await apiClient.fetch(`/api/personas`, {
      method: 'POST',
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      throw new Error(`Failed to create persona: ${response.statusText}`)
    }

    const created = await response.json()
    const persona: Persona = {
      id: created.id,
      userId: created.user_id || undefined,
      name: created.name,
      description: created.description,
      avatarUrl: created.avatar_url,
      presentationMode: created.presentation_mode,
      voiceConfig: voiceConfigFromApi(created.voice_config),
      avatarConfig: transformAvatarConfig(created.avatar_config),
      ragConfig: {
        systemPrompt: created.rag_config.system_prompt,
        temperature: created.rag_config.temperature,
        maxTokens: created.rag_config.max_tokens,
        topK: created.rag_config.top_k,
        scoreThreshold: created.rag_config.score_threshold,
        serverUrl: created.rag_config.server_url,
        model: created.rag_config.model,
        apiKey: created.rag_config.api_key,
        graphId: created.rag_config?.graph_id,
      },
      currentBundleId: created.current_bundle_id ?? null,
      whatsappConfig: transformWhatsAppConfig(created.whatsapp_config),
      paymentConfig: transformPaymentConfig(created.payment_config),
      isPublic: created.is_public || false,
      isLocked: created.is_locked || false,
      tags: mapTagsFromApi(created.tags),
      source: created.source || 'manual',
      externalId: created.external_id || undefined,
      externalMetadata: created.external_metadata || undefined,
      createdAt: new Date(created.created_at).getTime(),
      updatedAt: new Date(created.updated_at).getTime(),
    }

    addPersona(persona)
    return persona
  }, [addPersona])

  const updatePersona = useCallback(async (
    id: string,
    data: PersonaEdit
  ): Promise<Persona> => {
    const body: Record<string, any> = {}

    if (data.name !== undefined) body.name = data.name
    if (data.description !== undefined) body.description = data.description
    if (data.avatarUrl !== undefined) body.avatar_url = data.avatarUrl
    if (data.presentationMode !== undefined) body.presentation_mode = data.presentationMode
    if (data.voiceConfig !== undefined) {
      body.voice_config = voiceConfigToApi(data.voiceConfig)
    }
    if (data.avatarConfig !== undefined) {
      body.avatar_config = data.avatarConfig ? serializeAvatarConfig(data.avatarConfig) : null
    }
    if (data.ragConfig !== undefined) {
      body.rag_config = {
        system_prompt: data.ragConfig.systemPrompt,
        temperature: data.ragConfig.temperature,
        max_tokens: data.ragConfig.maxTokens,
        top_k: data.ragConfig.topK,
        score_threshold: data.ragConfig.scoreThreshold,
        server_url: data.ragConfig.serverUrl || null,
        model: data.ragConfig.model || null,
        api_key: data.ragConfig.apiKey || null,
        graph_id: data.ragConfig.graphId || null,
      }
    }
    if (data.whatsappConfig !== undefined) {
      body.whatsapp_config = data.whatsappConfig ? serializeWhatsAppConfig(data.whatsappConfig) : null
    }
    if (data.paymentConfig !== undefined) {
      body.payment_config = data.paymentConfig ? serializePaymentConfig(data.paymentConfig) : null
    }
    if (data.isPublic !== undefined) body.is_public = data.isPublic
    if (data.isLocked !== undefined) body.is_locked = data.isLocked
    if (data.tags !== undefined) body.tags = data.tags

    const response = await apiClient.fetch(`/api/personas/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      throw new Error(`Failed to update persona: ${response.statusText}`)
    }

    await response.json()
    const stored: Partial<Persona> = {
      ...data,
      whatsappConfig: data.whatsappConfig ?? undefined,
      paymentConfig: data.paymentConfig ?? undefined,
    }
    if (!('whatsappConfig' in data)) delete stored.whatsappConfig
    if (!('paymentConfig' in data)) delete stored.paymentConfig
    updatePersonaInStore(id, stored)

    return { ...usePersonaStore.getState().personas[id], ...stored }
  }, [updatePersonaInStore])

  const deletePersona = useCallback(
    async (id: string, opts?: { hard?: boolean }): Promise<void> => {
      const url = opts?.hard
        ? `/api/personas/${id}?hard=true`
        : `/api/personas/${id}`
      const response = await apiClient.fetch(url, { method: 'DELETE' })

      if (!response.ok) {
        throw new Error(`Failed to delete persona: ${response.statusText}`)
      }

      // Hard delete removes the row entirely; soft delete also removes it
      // from the store so it disappears from the default list. Callers that
      // need to show the archived view should refetch with includeArchived.
      deletePersonaFromStore(id)
    },
    [deletePersonaFromStore]
  )

  const restorePersona = useCallback(
    async (id: string): Promise<Persona> => {
      const response = await apiClient.fetch(`/api/personas/${id}/restore`, {
        method: 'POST',
      })

      if (!response.ok) {
        throw new Error(`Failed to restore persona: ${response.statusText}`)
      }

      const restored = await response.json()
      const persona: Persona = {
        id: restored.id,
        userId: restored.user_id || undefined,
        name: restored.name,
        description: restored.description,
        avatarUrl: restored.avatar_url,
        presentationMode: restored.presentation_mode,
        voiceConfig: voiceConfigFromApi(restored.voice_config),
        avatarConfig: restored.avatar_config || undefined,
        ragConfig: {
          systemPrompt: restored.rag_config.system_prompt,
          temperature: restored.rag_config.temperature,
          maxTokens: restored.rag_config.max_tokens,
          topK: restored.rag_config.top_k,
          scoreThreshold: restored.rag_config.score_threshold,
          serverUrl: restored.rag_config.server_url,
          model: restored.rag_config.model,
          apiKey: restored.rag_config.api_key,
          graphId: restored.rag_config?.graph_id,
        },
        currentBundleId: restored.current_bundle_id ?? null,
        whatsappConfig: restored.whatsapp_config || undefined,
        isPublic: restored.is_public || false,
        isLocked: restored.is_locked || false,
        tags: mapTagsFromApi(restored.tags),
        source: restored.source || 'manual',
        externalId: restored.external_id || undefined,
        externalMetadata: restored.external_metadata || undefined,
        archivedAt: restored.archived_at
          ? new Date(restored.archived_at).getTime()
          : undefined,
        createdAt: new Date(restored.created_at).getTime(),
        updatedAt: new Date(restored.updated_at).getTime(),
      }
      addPersona(persona)
      return persona
    },
    [addPersona]
  )

  const duplicatePersona = useCallback(
    async (id: string): Promise<Persona> => {
      const response = await apiClient.fetch(`/api/personas/${id}/duplicate`, {
        method: 'POST',
      })

      if (!response.ok) {
        throw new Error(`Failed to duplicate persona: ${response.statusText}`)
      }

      const created = await response.json()
      const persona: Persona = {
        id: created.id,
        userId: created.user_id || undefined,
        name: created.name,
        description: created.description,
        avatarUrl: created.avatar_url,
        presentationMode: created.presentation_mode,
        voiceConfig: voiceConfigFromApi(created.voice_config),
        avatarConfig: transformAvatarConfig(created.avatar_config),
        ragConfig: {
          systemPrompt: created.rag_config.system_prompt,
          temperature: created.rag_config.temperature,
          maxTokens: created.rag_config.max_tokens,
          topK: created.rag_config.top_k,
          scoreThreshold: created.rag_config.score_threshold,
          serverUrl: created.rag_config.server_url,
          model: created.rag_config.model,
          apiKey: created.rag_config.api_key,
          graphId: created.rag_config?.graph_id,
        },
        currentBundleId: created.current_bundle_id ?? null,
        whatsappConfig: transformWhatsAppConfig(created.whatsapp_config),
        isShared: created.is_shared || false,
        hasShares: created.has_shares || false,
        isPublic: created.is_public || false,
        isLocked: created.is_locked || false,
        tags: mapTagsFromApi(created.tags),
        source: created.source || 'manual',
        externalId: created.external_id || undefined,
        externalMetadata: created.external_metadata || undefined,
        archivedAt: created.archived_at
          ? new Date(created.archived_at).getTime()
          : undefined,
        createdAt: new Date(created.created_at).getTime(),
        updatedAt: new Date(created.updated_at).getTime(),
      }
      addPersona(persona)
      return persona
    },
    [addPersona]
  )

  const uploadPersonaAvatar = useCallback(
    async (id: string, file: File): Promise<Persona> => {
      const fd = new FormData()
      fd.append('file', file)
      const response = await apiClient.fetch(`/api/personas/${id}/avatar`, { method: 'POST', body: fd })
      if (!response.ok) throw new Error(`Failed to upload avatar: ${response.statusText}`)
      const data = await response.json()
      updatePersonaInStore(id, { avatarUrl: data.avatar_url ?? undefined })
      return { ...usePersonaStore.getState().personas[id], avatarUrl: data.avatar_url ?? undefined }
    },
    [updatePersonaInStore]
  )

  const clearPersonaAvatar = useCallback(
    async (id: string): Promise<void> => {
      const response = await apiClient.fetch(`/api/personas/${id}/avatar`, { method: 'DELETE' })
      if (!response.ok) throw new Error(`Failed to clear avatar: ${response.statusText}`)
      updatePersonaInStore(id, { avatarUrl: undefined })
    },
    [updatePersonaInStore]
  )

  // Clear a persona's edit lock. Owner or admin (break-glass) — the backend
  // authorizes; locking itself stays owner-only via updatePersona(isLocked:true).
  const unlockPersona = useCallback(
    async (id: string): Promise<void> => {
      const response = await apiClient.fetch(`/api/personas/${id}/unlock`, { method: 'POST' })
      if (!response.ok) throw new Error(`Failed to unlock persona: ${response.statusText}`)
      await response.json()
      updatePersonaInStore(id, { isLocked: false })
    },
    [updatePersonaInStore]
  )

  // Fetch personas on mount
  useEffect(() => {
    fetchPersonas()
  }, [fetchPersonas])

  return {
    personas: Object.values(personas),
    isLoading,
    error,
    fetchPersonas,
    createPersona,
    updatePersona,
    deletePersona,
    restorePersona,
    duplicatePersona,
    uploadPersonaAvatar,
    clearPersonaAvatar,
    unlockPersona,
  }
}
